import React, { useRef, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Grid,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../hooks/useAppDispatch';

import AuthorizedSignCheckbox from '../authorized-sign-checkbox/AuthorizedSignCheckbox';
import styles from './CreateSupplierContactModal.module.scss';
import { createSupplierContact } from '../../slices/suppliers/suppliersSlice';
import { SupplierContact } from '../../models/supplier.contact.model';
import { toast } from 'react-toastify';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

interface CreateContactModalProps {
  open: boolean;
  onClose: () => void;
  onSuccessfulSubmit?: () => void;
  supplierId: number;
  companyId: number;
}

export default function CreateSupplierContactModal({
  open,
  onClose,
  onSuccessfulSubmit,
  supplierId,
  companyId,
}: CreateContactModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLDivElement | null>(null);

  const [formData, setFormData] = useState({
    full_name: '',
    email_address: '',
    phone_number: '',
    job_title: '',
    postal_address: '',
    authorized_rep: false,
    supplier_id: supplierId,
  });

  const handleChange =
    <K extends keyof typeof formData>(field: K) =>
    (value: (typeof formData)[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleCreate = () => {
    dispatch(showOverlay());
    const errors = {
      full_name: !formData.full_name.trim(),
    };

    if (Object.values(errors).some((error) => error)) {
      console.error('Required fields are empty');
      return;
    }
    dispatch(
      createSupplierContact({
        supplierId: supplierId,
        contact: formData as Omit<SupplierContact, 'id'>,
        company_id: companyId,
      })
    )
      .unwrap()
      .finally(() => dispatch(hideOverlay()))
      .catch((error) => toast.error(error.message ? error.message : t('messages.errorOccurred')));
    onSuccessfulSubmit();
  };

  return (
    <Modal open={open} onClose={onClose} className={'action-modal'} closeAfterTransition>
      <Grid container className={styles.modal_container}>
        <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
          <Card ref={formRef}>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant='h6' gutterBottom>
                {t('businessProfile.createContact')}
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.fullName')} *`}
                    variant='outlined'
                    fullWidth
                    value={formData.full_name ?? ''}
                    onChange={(e) => handleChange('full_name')(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={`${t('formInputs.emailAddressInputLabel')}`}
                    variant='outlined'
                    type='email'
                    fullWidth
                    value={formData.email_address ?? ''}
                    onChange={(e) => handleChange('email_address')(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.phoneNumber')}`}
                    variant='outlined'
                    type='tel'
                    fullWidth
                    value={formData.phone_number ?? ''}
                    onChange={(e) => handleChange('phone_number')(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.jobTitle')}`}
                    variant='outlined'
                    fullWidth
                    value={formData.job_title ?? ''}
                    onChange={(e) => handleChange('job_title')(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.postalAddress')}`}
                    variant='outlined'
                    fullWidth
                    value={formData.postal_address ?? ''}
                    onChange={(e) => handleChange('postal_address')(e.target.value)}
                  />
                </Grid>
              </Grid>

              <Grid container className={styles.checkbox_container}>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <AuthorizedSignCheckbox
                    authorizedRep
                    value={formData.authorized_rep}
                    onChange={handleChange('authorized_rep')}
                    disabled={false}
                  />
                </Grid>
              </Grid>
              <Stack direction='row' gap={'8px'} justifyContent='flex-end' marginTop={'16px'}>
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic btn-cancel'
                  onClick={onClose}
                >
                  {t('buttons.close')}
                </Button>
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic'
                  onClick={handleCreate}
                >
                  {t('buttons.create')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
}
